<template>
    <div class="weight-field-display-edit">
        <div v-if="readonly">
            {{ value || '-' }}
        </div>
        <div v-else>
            <div v-if="showInput">
                <b-form-input
                    ref="input"
                    type="text"
                    :value="inputValue"
                    class="weight-input"
                    inputmode="decimal"
                    @input="onInput"
                    @blur="onBlur"
                    @keyup.enter="onEnter"
                />
            </div>
            <button
                v-else
                type="button"
                class="field-link"
                @click="openEditor"
            >
                {{ value }}
            </button>
        </div>
    </div>
</template>

<script>
import { BFormInput } from 'bootstrap-vue'

export default {
    name: 'WeightFieldDisplayEdit',
    components: {
        BFormInput,
    },
    props: {
        value: {
            type: [String, Number, null],
            default: '',
        },
        readonly: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            showInput: !this.value,
            inputValue: this.value,
            inputDebounceTimer: null,
        }
    },
    watch: {
        value(next) {
            this.inputValue = next
            if (!next) {
                this.showInput = true
                return
            }
            if (!this.isValidWeightInput(next)) {
                this.showInput = true
            }
        },
    },
    methods: {
        onInput(next) {
            const sanitized = this.sanitizeInput(next)
            this.inputValue = sanitized
            if (this.inputDebounceTimer) {
                clearTimeout(this.inputDebounceTimer)
            }
            this.inputDebounceTimer = setTimeout(() => {
                this.$emit('input', this.inputValue)
                this.inputDebounceTimer = null
            }, 2000)
        },
        flushPendingInput() {
            if (!this.inputDebounceTimer) {
                return
            }
            clearTimeout(this.inputDebounceTimer)
            this.inputDebounceTimer = null
            this.$emit('input', this.inputValue)
        },
        onBlur() {
            this.flushPendingInput()
            if (this.inputValue && String(this.inputValue).trim() && this.isValidWeightInput(this.inputValue)) {
                this.showInput = false
            }
        },
        onEnter() {
            this.flushPendingInput()
            if (this.inputValue && String(this.inputValue).trim() && this.isValidWeightInput(this.inputValue)) {
                this.showInput = false
            }
        },
        openEditor() {
            this.showInput = true
            this.inputValue = this.value
            this.$nextTick(() => {
                if (this.$refs.input && this.$refs.input.focus) {
                    this.$refs.input.focus()
                }
            })
        },
        sanitizeInput(value) {
            if (value === null || value === undefined) {
                return value
            }
            const raw = String(value)
                .replace(/–|—/g, '-')

            const chars = raw.split('')
            let separatorIndex = -1
            const hasDigitBefore = index => {
                for (let i = index - 1; i >= 0; i -= 1) {
                    if (/\d/.test(chars[i])) {
                        return true
                    }
                    if (!/\s/.test(chars[i])) {
                        return false
                    }
                }
                return false
            }

            for (let i = 0; i < chars.length; i += 1) {
                if (chars[i] === '-') {
                    if (separatorIndex === -1 && hasDigitBefore(i)) {
                        separatorIndex = i
                    } else {
                        chars[i] = ''
                    }
                }
            }

            return chars.join('')
        },
        isValidWeightInput(value) {
            if (value === null || value === undefined) {
                return false
            }
            const raw = String(value).trim()
            if (!raw) {
                return false
            }
            const normalized = raw
                .replace(/–|—/g, '-')
                .replace(/,/g, '.')
                .replace(/\s+/g, ' ')
            return /^(\d+(?:\.\d+)?)(?:\s*-\s*(\d+(?:\.\d+)?))?$/.test(normalized)
        },
    },
    beforeDestroy() {
        if (this.inputDebounceTimer) {
            clearTimeout(this.inputDebounceTimer)
            this.inputDebounceTimer = null
        }
    },
}
</script>

<style scoped>
.field-link {
    background: transparent;
    border: 0;
    padding: 0;
    color: #2b6cb0;
    text-decoration: underline;
    cursor: pointer;
    text-align: left;
}
.field-link:hover {
    color: #1e4e8c;
}
.weight-input {
    min-width: 140px;
    max-width: 160px;
}
</style>
